/* eslint-disable max-lines */
// Hooks
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import {
  query, queryData, queryVars,
  useAddChallengeMutation,
  useAddProjectMutation,
  useCopyChallengeMutation,
  useCopyProjectMutation,
  useDeleteChallengeMutation,
  useDeleteProjectMutation,
  useImportChallengeCsvMutation,
  useMoveChallengeMutation,
  getCompetenceTestTemplateUrlQuery,
  competenciesQuery,
} from "./index.graphql";
import {useHistory} from "react-router";
import useSearchParamsState from "../../hooks/useSearchParamsState";
import {useTour} from "../../providers/tour";

// Libs, types and consts
import {reverse} from "../../routing";
import {getLanguageCode} from "../../i18n";
import {Project, ChallengeMode, LanguageCode, Challenge, ChallengeFilterInput, ChallengeInput, Competence
} from "../../schema";
import {analyticsSendEvent} from "../../libs/analytics";
import {TABLE_ROWS_PER_PAGE} from "../../settings";
import {getDataWithMock, runTourOnFirstOpen} from "../../tours";

// Components
import {useModal} from "../../components/ModalProvider";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import Button from "../../ui/button";
import Paginator from "../../ui/paginator";
import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Breadcrumbs from "../../ui/breadcrumbs";
import {AddIcon} from "../../ui/icons";
import ChallengesTable from "../../components/editor/challenges-table/ChallengesTable";
import ChallengesFilterForm from "../../components/editor/challenges-table/ChallengesFilterForm";
import DeleteModal from "../../components/editor/DeleteModal";
import EditorIndexTour, {
  classes as tourClasses,
  useMockData,
  NAME as tourName,
  SUBNAMES as subToursNames,
} from "./index.tour";
import {SUBNAMES as layoutTourSubnames} from "../../components/Layout.tour";

// Classes
import cn from "classnames";
import classes from "./index.module.css";
import {useCurrentUser} from "../../App.context";
import ImportFileModal from "../../components/editor/importChallengeModal";

const SEARCH_PARAMS_CONFIG: {
  name: { type: "string" };
  challengeMode: { type: "string" };
  languageCode: { type: "string" };
  competenceIds: { type: "string" };
} = {
  "name": {type: "string"},
  "challengeMode": {type: "string"},
  "languageCode": {type: "string"},
  "competenceIds": {type: "string"},
}

export default function EditorIndexScene() {
  const history = useHistory();

  const {t, i18n} = useTranslation();
  const [filter, setFilter] = useSearchParamsState(SEARCH_PARAMS_CONFIG);

  const {runningTourName, bindToPage: bindTourToPage, run: runTour} = useTour();
  const tourMode = !!runningTourName && [tourName, subToursNames.openChallenge].includes(runningTourName);
  const mockData = useMockData();

  useEffect(() => {
    analyticsSendEvent("editorView");
  }, []);

  useEffect(() => bindTourToPage(tourName, subToursNames.openChallenge), [bindTourToPage]);

  useEffect(() => {
    runTourOnFirstOpen(tourName, runTour, [layoutTourSubnames.header]);
  }, [runTour]);
  const {data: competenciesData} = useQuery<{competencies: Competence[]}>(competenciesQuery);
  const competencies = competenciesData?.competencies ?? [];

  const {data, loading: fetching} = useQuery<queryData, queryVars>(query, {
    variables: {
      filter: {
        ...filter,
        challengeMode: filter.challengeMode === "" ? null : filter.challengeMode as ChallengeMode,
        languageCode: filter.languageCode === "" ? null : filter.languageCode as LanguageCode,
        competenceIds: filter.competenceIds
        ? filter.competenceIds.split(",").filter(Boolean)
        : undefined,
      }
    },
    fetchPolicy: "cache-and-network",
  });
  const handleFilterSave = useCallback((data: ChallengeFilterInput) => {
    setFilter({
      name: data.name ?? "",
      challengeMode: data.challengeMode ?? "",
      languageCode: data.languageCode ?? "",
      competenceIds: data.competenceIds?.join(",") ?? "",
    });
  }, [setFilter]);

  const {data: templateUrlData} = useQuery<{templateUrl: string}>(getCompetenceTestTemplateUrlQuery);
  const templateUrl = templateUrlData?.templateUrl;

  const [addChallenge] = useAddChallengeMutation();
  const [addProject] = useAddProjectMutation();
  const [copyChallenge] = useCopyChallengeMutation();
  const [deleteChallenge] = useDeleteChallengeMutation();
  const [importChallengeCsv] = useImportChallengeCsvMutation();
  const [copyProject] = useCopyProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [moveChallenge] = useMoveChallengeMutation();

  const onAddChallenge = useCallback(() => {
    addChallenge({
      variables: {
        data: {
          name: t("editorIndex.newChallengeName"),
          languageCode: getLanguageCode(i18n.language),
        },
      },
    }).then(({data}) => {
      const challenge = data?.challenge!;

      analyticsSendEvent("editorAddChallenge", {
        challengeId: challenge.id,
      });
      history.push(reverse("editorChallengeEdit", {
        id: challenge.id
      }))
    })
  }, [history, addChallenge, t, i18n.language]);

    const onAddProject = useCallback(() => {
    addProject({
      variables: {
        data: {
          name: t("editorIndex.newProjectName"),
          languageCode: getLanguageCode(i18n.language)
        }
      }
    }).then(({data}) => {
      const project = data?.project!;

      analyticsSendEvent("editorAddProject", {
        projectId: project.id
      });
    })
  }, [addProject, t, i18n.language]);

  const onChallengeCopy = useCallback(
    (challengeId: Challenge["id"]) => {
      copyChallenge({
        variables: {id: challengeId},
      }).then(({data}) => {
        const challenge = data?.challenge!;

        analyticsSendEvent("editorCopyChallenge", {
          challengeId: challenge.id,
        });
      });
    },[copyChallenge]);

  const onProjectCopy = useCallback((projectId: Project["id"]) => {
    copyProject({
      variables: {id: projectId}
    }).then(({data}) => {
      const project = data?.project!;

      analyticsSendEvent("editorCopyProject", {
        projectId: project.id
      });
    })
  }, [copyProject]);

  const {add: addModal} = useModal();
  const onChallengeDelete = useCallback(
    (challengeId: Challenge["id"]) => {
      const onDelete = () => {
        analyticsSendEvent("editorChallengeDelete", {
          challengeId: challengeId,
        });
        deleteChallenge({
          variables: {id: challengeId},
        });
        modal.remove();
      };

      const onCancel = () => modal.remove();

    const modal = addModal({
      id: `deleteChallenge_${challengeId}`,
      header: t("editorIndex.deleteChallenge"),
      content: t("editorIndex.deleteChallengeConfirmation"),
      footer: <DeleteModal.Footer onConfirm={onDelete} onCancel={onCancel}/>
    });
  }, [t, deleteChallenge, addModal]);

    const onProjectDelete = useCallback((projectId: Project["id"]) => {
    const onDelete = () => {
      analyticsSendEvent("editorProjectDelete", {
        projectId: projectId,
      });
      deleteProject({
        variables: {id: projectId}
      });
      modal.remove()
    };

    const onCancel = () => modal.remove();

    const modal = addModal({
      id: `deleteProject_${projectId}`,
      header: t("editorIndex.deleteProject"),
      content: t("editorIndex.deleteProjectConfirmation"),
      footer: <DeleteModal.Footer onConfirm={onDelete} onCancel={onCancel}/>
    });
  }, [t, deleteProject, addModal]);

  const onChallengeMove = useCallback((challengeId: string, data: Pick<ChallengeInput, "projectId">) => {
    analyticsSendEvent("editorChallengeUpdate", {challengeId});

    moveChallenge({
      variables: {id: challengeId, data}
    })
  }, [moveChallenge]);

  const {items, projects} = useMemo(() => (
    getDataWithMock(data, mockData, tourMode)
  ), [data, mockData, tourMode]);
  const loading = !tourMode && (!items && fetching);

  const [currentPage, setCurrentPage] = useState(1);

  const itemsOnPage = Math.min(items?.length ?? 0, TABLE_ROWS_PER_PAGE);
  const totalItems = items?.length ?? 0;

  const totalPages = Math.ceil(totalItems / TABLE_ROWS_PER_PAGE);

  const isExperimental = useCurrentUser()?.experimentalMode;

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const currentPageItems = useMemo(
    () => items?.slice((currentPage - 1) * TABLE_ROWS_PER_PAGE, currentPage * TABLE_ROWS_PER_PAGE),
    [items, currentPage],
  );

  const onImportChallengeModal = () => {
    const modal = addModal({
      header: <ImportFileModal.Header />,
      content: (
        <ImportFileModal.Content
          onImportFile={(file) => {
            handleUploadChallenge(file, modal);
          }}
          templateUrl={templateUrl}
        />
      ),
    });
  };

  const handleUploadChallenge = async (file: File, modal: { remove: () => void }) => {
    try {
      const languageCode = getLanguageCode(i18n.language);
      await importChallengeCsv({variables: {attachment: file, languageCode}});
      modal.remove();
    } catch (error) {
      console.error("Error importing challenge:", error);
      modal.remove();
    }
  };

  return (
    <>
      <ReactDocumentTitle title={t("editorIndex.title")} />

      {tourMode && <EditorIndexTour key={runningTourName} loaded={!loading} />}
      <Layout data-testid="editorIndexScene">
        <Header />
        <Main>
          <Container className={classes.main}>
            <Breadcrumbs className={classes.breadcrumbs}>
              <span>{t("breadcrumbs.editor")}</span>
            </Breadcrumbs>

            <div className={classes.header}>
              <h1 className={classes.h1}>{t("editorIndex.h1")}</h1>
              <div className={classes.topRightMenu}>
                {!loading && (
                  <>
                    <Button
                      className={cn(classes.addButton, tourClasses.addProjectButton)}
                      onClick={onAddProject}
                      color="primary"
                    >
                      <AddIcon/>{t("editorIndex.addProject")}
                    </Button>
                    {isExperimental && (
                    <Button className={cn(classes.addButton)}
                    //вот сюда добавить кнопку Импорт csv файла
                    onClick={onImportChallengeModal}
                    color="primary">
                    <AddIcon />
                      {t("editorIndex.importChallenge")}
                    </Button>
                    )}
                    <Button
                      className={cn(classes.addButton, tourClasses.addChallengeButton)}
                      onClick={onAddChallenge}
                      color="primary"
                    >
                      <AddIcon />
                      {t("editorIndex.addChallenge")}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className={classes.content}>
              <div className={classes.contentMain}>
                <div className={classes.tableRoot}>
                  {(!loading && currentPageItems && projects) ? (
                    <ChallengesTable
                      className={tourClasses.challengeTable}
                      data={currentPageItems}
                      projects={projects}
                      onChallengeCopy={onChallengeCopy}
                      onChallengeDelete={onChallengeDelete}
                      onProjectCopy={onProjectCopy}
                      onProjectDelete={onProjectDelete}
                      onChallengeMove={onChallengeMove}
                    />
                  ) : (
                    <ChallengesTable.Skeleton />
                  )}
                </div>

                <div className={classes.paginatorRow}>
                  <div className={classes.paginatorHelpText}>
                    {t("common.paginatorShowingItems", {
                      items: itemsOnPage,
                      totalItems,
                    })}
                  </div>

                  {totalPages > 1 && (
                    <Paginator
                      className={classes.paginator}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </div>
              </div>
              <div className={classes.contentAside}>
                <ChallengesFilterForm
                  className={tourClasses.filterForm}
                  initialValues={{
                    ...filter,
                    challengeMode: filter.challengeMode as ChallengeMode,
                    languageCode: filter.languageCode as LanguageCode,
                    competenceIds: filter.competenceIds
                      ? filter.competenceIds.split(",").filter(Boolean) as string[]
                      : undefined,
                  }}
                  onSave={handleFilterSave}
                  competencies={competencies}
                />
              </div>
            </div>
          </Container>
        </Main>
      </Layout>
    </>
  );
}
